import { useUnit } from 'effector-react'
import { Link } from 'react-aria-components'

import { css } from '@portal/styled-system/css'

import { type Translation, useI18n } from '@/shared/lib/i18n'
import { getLegalLinkHref } from '@/shared/lib/legal-link'
import { CancelButton, Modal, type ModalProps, ProceedButton } from '@/shared/ui'

import { acceptTermsButtonClicked } from './model'

type AcceptTermsDialogProps = ModalProps & {
    onAcceptButtonClick: () => void
}

const linkMap = {
    LINK_LEGAL_TERMS: 'terms-of-service/',
    LINK_LEGAL_PRIVACY_TO: 'privacy-policy/',
    LINK_LEGAL_COOKIES: 'cookies/',
}

export function AcceptTermsDialog(props: AcceptTermsDialogProps) {
    const { i18n, lang } = useI18n()

    const acceptTerms = useUnit(acceptTermsButtonClicked)

    const handleAcceptButtonClick = () => {
        acceptTerms()

        props.onAcceptButtonClick()
    }

    return (
        <Modal {...props}>
            <Modal.CloseButton onClose={props.onClose} />
            <Modal.Header styles={css.raw({ backgroundColor: 'info' })} icon="fa-solid fa-book-open-lines">
                {i18n.PORTAL_TERMS_AND_POLICIES}
            </Modal.Header>
            <Modal.Body styles={css.raw({ gap: '3', alignItems: 'flex-start', textAlign: 'start' })}>
                {i18n.TEXT_LEGAL_ACCEPT}
                <ul
                    className={css({
                        marginBottom: '0',
                        paddingLeft: '[1.75rem]',
                        listStyle: 'disc',
                        '& li::marker': { color: 'gray-v2.400' },
                    })}
                >
                    {Object.entries(linkMap).map(([key, value]) => (
                        <li key={key}>
                            <Link
                                href={getLegalLinkHref(lang, value)}
                                target="_blank"
                                className={css({
                                    borderRadius: 'small',
                                    color: 'cyan-v2.400',
                                    _hover: { textDecoration: 'none', color: 'cyan-v2.600' },
                                    _pressed: { color: 'cyan-v2.700' },
                                    _focusVisible: {
                                        paddingX: '2',
                                        outline: 'none',
                                        color: 'cyan-v2.700',
                                        boxShadow: '[0 0 0 0.25rem token(colors.cyan-v2/50)]',
                                    },
                                })}
                                rel="noopener noreferrer"
                            >
                                {i18n[key as keyof Translation]}
                            </Link>
                        </li>
                    ))}
                </ul>
            </Modal.Body>
            <Modal.Buttons>
                <CancelButton onPress={props.onClose} styles={css.raw({ width: 'auto' })} />
                <ProceedButton onPress={handleAcceptButtonClick}>{i18n.BUTTON_ACCEPT}</ProceedButton>
            </Modal.Buttons>
        </Modal>
    )
}
